import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { api, payload } from "./lib/api";
import { useAccessibleDialog } from "./lib/useAccessibleDialog";
import type { Member, Page, Site, User } from "./types";
import universeMark from "../../Universe/favicon-512.png";
import VisualEditor from "./VisualEditor";
import LiveSeoPanel from "./LiveSeoPanel";
import SafeContentEditor from "./SafeContentEditor";
import RestaurantMenuManager from "./RestaurantMenuManager";
import SiteHome from "./SiteHome";
import RestaurantOperations from "./RestaurantOperations";
import EscapeOperations from "./EscapeOperations";
import RentalOperations from "./RentalOperations";
import SocialGalleryManager from "./SocialGalleryManager";
import AnalyticsPanel from "./AnalyticsPanel";
import ModulesPanel from "./ModulesPanel";
import SeoIntelligencePanel from "./SeoIntelligencePanel";
import LeadsPage from "./LeadsPage";

function UniverseLogo({ light = false }: { light?: boolean }) {
  return (
    <span className={`brand ${light ? "brand-light" : ""}`}>
      <img src={universeMark} alt="" />{" "}
      <span className="brand-word">UNIVERSE</span>
    </span>
  );
}

function Icon({
  name,
}: {
  name:
    "arrow" | "plus" | "pages" | "globe" | "settings" | "duplicate" | "trash"
    | "home" | "chart" | "calendar" | "hourglass" | "image" | "menu" | "users" | "search" | "back";
}) {
  const paths = {
    home: <path d="M3 11.5 12 4l9 7.5M5.5 9.5V20h13V9.5M10 20v-5.5h4V20" />,
    chart: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" />
      </>
    ),
    hourglass: <path d="M7 3h10M7 21h10M8 3c0 5 8 5 8 9s-8 4-8 9M16 3c0 5-8 5-8 9s8 4 8 9" />,
    image: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="9" cy="10" r="2" />
        <path d="m21 16-5-5-8 9" />
      </>
    ),
    menu: <path d="M5 4h14v16H5zM9 9h6M9 13h6M9 17h3" />,
    users: (
      <>
        <circle cx="9" cy="8" r="3.5" />
        <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M18 14.5a6 6 0 0 1 3.5 5.5" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </>
    ),
    back: <path d="M15 18 9 12l6-6" />,
    arrow: <path d="m9 18 6-6-6-6" />,
    plus: <path d="M12 5v14M5 12h14" />,
    pages: (
      <path d="M8 4h9a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm-3 3H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h1" />
    ),
    globe: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" />
      </>
    ),
    duplicate: (
      <>
        <rect x="8" y="8" width="11" height="11" rx="2" />
        <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
      </>
    ),
    trash: (
      <>
        <path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6" />
      </>
    ),
  };
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}

const WORKSPACE_SECTIONS = [
  "home",
  "analytics",
  "pages",
  "menu",
  "bookings",
  "marketing",
  "escape",
  "rental",
  "social",
  "seo",
  "settings",
] as const;
type WorkspaceSection = (typeof WORKSPACE_SECTIONS)[number];

function isMenuPage(page: Page) {
  return /menu|carta|piatti|cucina|wine|drink/i.test(`${page.title} ${page.slug}`);
}

function sectionFromSearch(search: string): WorkspaceSection {
  const params = new URLSearchParams(search);
  const asked = params.get("section");
  if (asked && (WORKSPACE_SECTIONS as readonly string[]).includes(asked))
    return asked as WorkspaceSection;
  if (params.get("instagram") === "connected") return "social";
  if (params.get("google") === "connected") return "seo";
  return "home";
}

function Notice({
  message,
  kind = "error",
}: {
  message: string;
  kind?: "error" | "success";
}) {
  return (
    <div className={`notice ${kind}`} role="status">
      {message}
    </div>
  );
}

function ClientEditor({ user }: { user: User }) {
  const { siteId = "" } = useParams();
  const [access, setAccess] = useState<{
    mode: "loading" | "legacy" | "visual";
    canEdit: boolean;
  }>({ mode: "loading", canEdit: false });
  useEffect(() => {
    api<{ site: Site }>(`/api/sites/${siteId}`)
      .then((result) =>
        setAccess({
          mode:
            result.site.editorMode === "protected" ? "legacy" : "visual",
          canEdit:
            user.role === "admin" ||
            result.site.memberRole === "admin" ||
            result.site.memberRole === "editor",
        }),
      )
      .catch(() => setAccess({ mode: "legacy", canEdit: false }));
  }, [siteId, user.role]);
  if (access.mode === "loading")
    return (
      <main className="app-loading">
        <img src={universeMark} alt="Universe" />
        <p>Preparazione editor protetto</p>
      </main>
    );
  return access.mode === "legacy" ? (
    <SafeContentEditor canEdit={access.canEdit} />
  ) : (
    <VisualEditor user={user} canEdit={access.canEdit} />
  );
}

function PasswordModal({ close }: { close: () => void }) {
  const dialogRef = useAccessibleDialog<HTMLFormElement>(true, close);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setSuccess("");
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    if (data.newPassword !== data.confirmPassword) {
      setError("Le nuove password non coincidono");
      setBusy(false);
      return;
    }
    try {
      await api(
        "/api/auth/password",
        payload("POST", {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        }),
      );
      form.reset();
      setSuccess(
        "Password aggiornata. Le altre sessioni sono state disconnesse.",
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Impossibile cambiare la password",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <form
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-dialog-title"
        tabIndex={-1}
        onSubmit={submit}
      >
        <button
          type="button"
          className="modal-close"
          onClick={close}
          aria-label="Chiudi cambio password"
        >
          ×
        </button>
        <p className="eyebrow indigo">Sicurezza account</p>
        <h2 id="password-dialog-title">Cambia password</h2>
        <p className="muted">
          Dopo il salvataggio, tutte le altre sessioni verranno disconnesse.
        </p>
        {error && <Notice message={error} />}
        {success && <Notice message={success} kind="success" />}
        <label>
          Password attuale
          <input
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            required
          />
        </label>
        <label>
          Nuova password
          <input
            name="newPassword"
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
          />
          <small>Almeno 12 caratteri</small>
        </label>
        <label>
          Conferma nuova password
          <input
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
          />
        </label>
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={close}>
            Chiudi
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "Aggiornamento…" : "Aggiorna password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ActivationScreen({
  token,
  onAuth,
}: {
  token: string;
  onAuth: (user: User) => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<{ name: string }>(`/api/activation?token=${encodeURIComponent(token)}`)
      .then((result) => setName(result.name))
      .catch((cause) => setError(cause.message));
  }, [token]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = Object.fromEntries(new FormData(event.currentTarget));
    if (data.password !== data.confirmPassword) {
      setError("Le password non coincidono");
      setBusy(false);
      return;
    }
    try {
      const result = await api<{ user: User }>(
        "/api/activation",
        payload("POST", { token, password: data.password }),
      );
      onAuth(result.user);
      navigate("/", { replace: true });
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Impossibile attivare l’account",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-layout">
      <section className="auth-story">
        <div>
          <UniverseLogo light />
        </div>
        <div>
          <p className="eyebrow">Accesso protetto</p>
          <h1>Scegli tu la tua password.</h1>
          <p>
            Il link è personale, scade automaticamente e può essere utilizzato
            una sola volta.
          </p>
        </div>
        <p className="auth-foot">Universe website system</p>
      </section>
      <section className="auth-panel">
        <form className="auth-card" onSubmit={submit}>
          <div className="mobile-brand">
            <UniverseLogo />
          </div>
          <p className="eyebrow indigo">Attivazione account</p>
          <h2>{name ? `Ciao, ${name}` : "Accesso sicuro"}</h2>
          <p className="muted">
            Imposta una password personale di almeno 12 caratteri.
          </p>
          {error && <Notice message={error} />}
          {!error && (
            <>
              <label>
                Nuova password
                <input
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                />
              </label>
              <label>
                Conferma password
                <input
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                />
              </label>
              <button className="primary wide" disabled={busy || !name}>
                {busy ? "Attivazione…" : "Attiva il mio account"}
              </button>
            </>
          )}
        </form>
      </section>
    </main>
  );
}

function AuthScreen({
  mode,
  onAuth,
}: {
  mode: "login" | "setup";
  onAuth: (user: User) => void;
}) {
  const needsSetup = mode === "setup";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const data = Object.fromEntries(form);
    try {
      const result = await api<{ user: User }>(
        needsSetup ? "/api/setup" : "/api/auth/login",
        payload("POST", data),
      );
      onAuth(result.user);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Operazione non riuscita",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-layout">
      <section className="auth-story">
        <Link to="/" aria-label="Universe">
          <UniverseLogo light />
        </Link>
        <div>
          <p className="eyebrow">Universe website system</p>
          <h1>Il tuo sito, sempre nelle tue mani.</h1>
          <p>
            Aggiorna contenuti, immagini, pagine e visibilità online in uno
            spazio progettato per il tuo business.
          </p>
        </div>
        <p className="auth-foot">
          Progettato a Fano per il lavoro reale di agenzia.
        </p>
      </section>
      <section className="auth-panel">
        <form className="auth-card" onSubmit={submit}>
          <div className="mobile-brand">
            <UniverseLogo />
          </div>
          <p className="eyebrow indigo">
            {needsSetup ? "Configurazione riservata" : "Area clienti"}
          </p>
          <h2>{needsSetup ? "Configura Universe" : "Accedi al tuo sito"}</h2>
          <p className="muted">
            {needsSetup
              ? "Crea l’amministratore principale. Questa pagina è riservata a Universe."
              : "Usa le credenziali ricevute da Universe per gestire il tuo sito."}
          </p>
          {error && <Notice message={error} />}
          {needsSetup && (
            <label>
              Nome
              <input
                name="name"
                autoComplete="name"
                minLength={2}
                required
                placeholder="Nome e cognome"
              />
            </label>
          )}
          <label>
            {needsSetup ? "Email" : "Email o nome utente"}
            <input
              name={needsSetup ? "email" : "identifier"}
              type={needsSetup ? "email" : "text"}
              autoComplete="username"
              required
              placeholder={
                needsSetup ? "nome@agenzia.it" : "Email o nome utente"
              }
            />
          </label>
          <label>
            Password
            <div className="password-field">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete={needsSetup ? "new-password" : "current-password"}
                minLength={needsSetup ? 12 : 1}
                required
                placeholder={
                  needsSetup ? "Almeno 12 caratteri" : "La tua password"
                }
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                aria-pressed={showPassword}
              >
                {showPassword ? "Nascondi" : "Mostra"}
              </button>
            </div>
            {needsSetup && (
              <small className="field-help">Usa almeno 12 caratteri.</small>
            )}
          </label>
          <button className="primary wide" disabled={busy}>
            {busy
              ? "Attendi…"
              : needsSetup
                ? "Crea spazio di lavoro"
                : "Accedi"}
          </button>
        </form>
      </section>
    </main>
  );
}

function Shell({
  user,
  onLogout,
  children,
}: {
  user: User;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const [passwordOpen, setPasswordOpen] = useState(false);
  const location = useLocation();
  const onLeads = location.pathname.startsWith("/leads");
  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="Navigazione principale">
        <Link to="/" aria-label="Universe">
          <UniverseLogo />
        </Link>
        <nav aria-label="Sezioni del CMS">
          <Link className={onLeads ? undefined : "active"} to="/">
            <Icon name="globe" />
            Siti
          </Link>
          {user.role === "admin" && (
            <Link className={onLeads ? "active" : undefined} to="/leads">
              <Icon name="users" />
              Leads
            </Link>
          )}
          <span className="nav-disabled">
            <Icon name="pages" />
            Libreria
          </span>
        </nav>
        <div className="sidebar-user">
          <span>{user.name.slice(0, 1).toUpperCase()}</span>
          <div>
            <strong>{user.name}</strong>
            <small>
              {user.role === "admin" ? "Amministratore" : "Cliente"}
            </small>
          </div>
          <div className="account-actions">
            <button
              onClick={() => setPasswordOpen(true)}
              title="Cambia password"
              aria-label="Cambia password"
            >
              <Icon name="settings" />
            </button>
            <button onClick={onLogout} title="Esci" aria-label="Esci">
              ↗
            </button>
          </div>
        </div>
      </aside>
      <div className="main-column">{children}</div>
      {passwordOpen && <PasswordModal close={() => setPasswordOpen(false)} />}
    </div>
  );
}

function CreateSiteModal({
  close,
  created,
  returnFocus,
}: {
  close: () => void;
  created: (site: Site) => void;
  returnFocus: HTMLButtonElement | null;
}) {
  const dialogRef = useAccessibleDialog<HTMLFormElement>(
    true,
    close,
    returnFocus,
  );
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  function makeSlug(value: string) {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{ site: Site }>(
        "/api/sites",
        payload("POST", { name, slug }),
      );
      created(result.site);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Errore");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
    >
      <form
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-site-dialog-title"
        tabIndex={-1}
        onSubmit={submit}
      >
        <button
          type="button"
          className="modal-close"
          onClick={close}
          aria-label="Chiudi creazione sito"
        >
          ×
        </button>
        <p className="eyebrow indigo">Nuovo progetto</p>
        <h2 id="create-site-dialog-title">Crea un sito</h2>
        <p className="muted">
          Parti dalla struttura essenziale. Potrai aggiungere contenuti e pagine
          subito dopo.
        </p>
        {error && <Notice message={error} />}
        <label>
          Nome del sito
          <input
            autoFocus
            data-dialog-initial-focus
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSlug(makeSlug(e.target.value));
            }}
            minLength={2}
            required
          />
        </label>
        <label>
          Identificativo
          <input
            value={slug}
            onChange={(e) => setSlug(makeSlug(e.target.value))}
            minLength={2}
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            required
          />
          <small>Usato internamente e per la pubblicazione.</small>
        </label>
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={close}>
            Annulla
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "Creazione…" : "Crea sito"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Dashboard({ user }: { user: User }) {
  const navigate = useNavigate();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [createTrigger, setCreateTrigger] =
    useState<HTMLButtonElement | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<{ sites: Site[] }>("/api/sites")
      .then((r) => {
        setSites(r.sites);
        if (user.role !== "admin" && r.sites.length === 1)
          navigate(`/sites/${r.sites[0].slug}`, { replace: true });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [navigate, user.role]);
  const admin = user.role === "admin";
  function closeCreateModal() {
    setModal(false);
    requestAnimationFrame(() => createTrigger?.focus());
  }
  return (
    <>
      <header className="topbar">
        <div>
          <p className="eyebrow indigo">
            {admin ? "Workspace Universe" : "Area cliente"}
          </p>
          <h1>{admin ? `Buongiorno, ${user.name.split(" ")[0]}` : "I tuoi progetti"}</h1>
        </div>
        {admin && (
          <button
            className="primary"
            onClick={(event) => {
              setCreateTrigger(event.currentTarget);
              setModal(true);
            }}
          >
            <Icon name="plus" />
            Nuovo sito
          </button>
        )}
      </header>
      <main className="content">
        <div className="section-intro">
          <p>
            {admin
              ? "Gestisci struttura, pagine e impostazioni da un solo spazio."
              : "Scegli il sito che vuoi aggiornare."}
          </p>
          <span>
            {sites.length} {sites.length === 1 ? "progetto" : "progetti"}
          </span>
        </div>
        {error && <Notice message={error} />}
        {loading ? (
          <div className="loading-grid">
            <i />
            <i />
            <i />
          </div>
        ) : sites.length === 0 ? (
          <div className="empty">
            <div className="empty-mark">
              <img src={universeMark} alt="" />
            </div>
            <h2>
              {admin ? "Il primo sito parte da qui" : "Nessun sito assegnato"}
            </h2>
            <p>
              {admin
                ? "Crea il progetto, aggiungi le pagine e prepara la sua struttura."
                : "Contatta Universe per collegare il tuo account al sito."}
            </p>
            {admin && (
              <button
                className="primary"
                onClick={(event) => {
                  setCreateTrigger(event.currentTarget);
                  setModal(true);
                }}
              >
                <Icon name="plus" />
                Crea il primo sito
              </button>
            )}
          </div>
        ) : (
          <div className="site-grid">
            {sites.map((site, index) => (
              <Link
                className="site-card"
                to={`/sites/${site.slug}`}
                key={site.id}
              >
                <SiteVisualPreview site={site} tone={index % 4} />
                <div className="site-info">
                  <div>
                    <span className={`status ${site.status}`}>
                      {site.status === "published"
                        ? "Online"
                        : site.status === "archived"
                          ? "Archiviato"
                          : "Bozza"}
                    </span>
                    <h2>{site.name}</h2>
                    <p>{site.domain || `${site.slug}.pages.dev`}</p>
                  </div>
                  <Icon name="arrow" />
                  <small>
                    {site.pageCount}{" "}
                    {site.pageCount === 1 ? "pagina" : "pagine"} · Aggiornato{" "}
                    {new Intl.DateTimeFormat("it", {
                      day: "2-digit",
                      month: "short",
                    }).format(new Date(site.updatedAt))}
                  </small>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      {modal && (
        <CreateSiteModal
          returnFocus={createTrigger}
          close={closeCreateModal}
          created={(site) => {
            setSites((old) => [site, ...old]);
            closeCreateModal();
          }}
        />
      )}
    </>
  );
}

function SiteVisualPreview({ site, tone }: { site: Site; tone: number }) {
  if (site.thumbnailUrl)
    return (
      <div className="site-preview has-thumbnail">
        <img src={site.thumbnailUrl} alt={`Anteprima di ${site.name}`} />
        <span className="preview-domain">
          {site.domain || `${site.slug}.pages.dev`}
        </span>
      </div>
    );
  return (
    <div className={`site-preview tone-${tone}`} aria-hidden="true">
      <div className="mini-browser-bar">
        <span />
        <span />
        <span />
        <small>{site.domain || site.slug}</small>
      </div>
      <div className="mini-site-nav">
        <strong>{site.name.slice(0, 1).toUpperCase()}</strong>
        <div><i /><i /><i /></div>
      </div>
      <div className="mini-site-hero">
        <section>
          <small>{site.name}</small>
          <b />
          <b />
          <i />
        </section>
        <div><span /><span /><span /></div>
      </div>
    </div>
  );
}

function PageEditor({
  page,
  close,
  saved,
  advanced,
}: {
  page: Page;
  close: () => void;
  saved: (page: Page) => void;
  advanced: boolean;
}) {
  const dialogRef = useAccessibleDialog<HTMLFormElement>(true, close);
  const [form, setForm] = useState(page);
  const [tab, setTab] = useState<"basics" | "social" | "advanced">("basics");
  const [structuredData, setStructuredData] = useState(
    JSON.stringify(page.structuredData, null, 2),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const googleVerification =
    form.additionalMeta.find((item) => item.name === "google-site-verification")
      ?.content || "";
  function setGoogleVerification(raw: string) {
    const fromTag = raw.match(/content=["']([^"']+)["']/i)?.[1];
    const content = (fromTag || raw).trim();
    const additionalMeta = form.additionalMeta.filter(
      (item) => item.name !== "google-site-verification",
    );
    if (content)
      additionalMeta.push({ name: "google-site-verification", content });
    setForm({ ...form, additionalMeta });
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const schema = JSON.parse(structuredData || "[]") as unknown;
      if (!Array.isArray(schema))
        throw new Error("I dati strutturati devono essere una lista JSON-LD");
      const result = await api<{ page: Page }>(
        `/api/pages/${page.id}`,
        payload("PATCH", {
          title: form.title,
          slug: form.slug,
          status: form.status,
          inNavigation: form.inNavigation,
          seoTitle: form.seoTitle || null,
          metaDescription: form.metaDescription || null,
          canonicalUrl: form.canonicalUrl || null,
          robots: form.robots,
          ogTitle: form.ogTitle || null,
          ogDescription: form.ogDescription || null,
          ogImageUrl: form.ogImageUrl || null,
          twitterCard: form.twitterCard,
          structuredData: schema,
          additionalMeta: form.additionalMeta,
        }),
      );
      saved(result.page);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Errore");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
    >
      <form
        ref={dialogRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="page-seo-dialog-title"
        tabIndex={-1}
        onSubmit={submit}
      >
        <div className="drawer-head">
          <div>
            <p className="eyebrow indigo">SEO della pagina</p>
            <h2 id="page-seo-dialog-title">{page.title}</h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Chiudi impostazioni SEO"
          >
            ×
          </button>
        </div>
        <div className="seo-tabs">
          <button
            type="button"
            className={tab === "basics" ? "active" : ""}
            onClick={() => setTab("basics")}
          >
            SEO di base
          </button>
          <button
            type="button"
            className={tab === "social" ? "active" : ""}
            onClick={() => setTab("social")}
          >
            Condivisione social
          </button>
          {advanced && (
            <button
              type="button"
              className={tab === "advanced" ? "active" : ""}
              onClick={() => setTab("advanced")}
            >
              SEO avanzata
            </button>
          )}
        </div>
        {error && <Notice message={error} />}
        {tab === "basics" && (
          <>
            <div className="search-preview">
              <small>Anteprima su Google</small>
              <strong>{form.seoTitle || form.title}</strong>
              <span>https://esempio.it/{form.slug}</span>
              <p>
                {form.metaDescription ||
                  "Aggiungi una descrizione per mostrare il contenuto della pagina nei risultati di ricerca."}
              </p>
            </div>
            <div className="field-pair">
              <label>
                Titolo pagina
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                />
              </label>
              <label>
                Slug URL
                <input
                  value={form.slug}
                  disabled={form.isHome}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      slug: e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, ""),
                    })
                  }
                />
              </label>
            </div>
            <label>
              Titolo per i motori di ricerca
              <input
                maxLength={70}
                value={form.seoTitle || ""}
                onChange={(e) => setForm({ ...form, seoTitle: e.target.value })}
              />
              <small>{(form.seoTitle || "").length}/70</small>
            </label>
            <label>
              Meta description
              <textarea
                maxLength={180}
                value={form.metaDescription || ""}
                onChange={(e) =>
                  setForm({ ...form, metaDescription: e.target.value })
                }
              />
              <small>{(form.metaDescription || "").length}/180</small>
            </label>
            <label>
              Indicizzazione
              <select
                value={form.robots}
                onChange={(e) =>
                  setForm({ ...form, robots: e.target.value as Page["robots"] })
                }
              >
                <option value="index,follow">Consenti l’indicizzazione</option>
                <option value="noindex,follow">
                  Non indicizzare, segui i link
                </option>
                <option value="noindex,nofollow">
                  Non indicizzare e non seguire
                </option>
              </select>
            </label>
          </>
        )}
        {tab === "social" && (
          <>
            <div className="social-preview">
              <div>
                {form.ogImageUrl ? (
                  <img src={form.ogImageUrl} alt="Anteprima social" />
                ) : (
                  <span>Anteprima immagine social</span>
                )}
              </div>
              <small>esempio.it</small>
              <strong>{form.ogTitle || form.seoTitle || form.title}</strong>
              <p>
                {form.ogDescription ||
                  form.metaDescription ||
                  "Descrizione della pagina per la condivisione."}
              </p>
            </div>
            <label>
              Immagine social
              <input
                type="url"
                value={form.ogImageUrl || ""}
                onChange={(e) =>
                  setForm({ ...form, ogImageUrl: e.target.value })
                }
                placeholder="https://…"
              />
            </label>
            <label>
              Titolo social
              <input
                maxLength={100}
                value={form.ogTitle || ""}
                onChange={(e) => setForm({ ...form, ogTitle: e.target.value })}
              />
            </label>
            <label>
              Descrizione social
              <textarea
                maxLength={300}
                value={form.ogDescription || ""}
                onChange={(e) =>
                  setForm({ ...form, ogDescription: e.target.value })
                }
              />
            </label>
            <label>
              Formato Twitter / X
              <select
                value={form.twitterCard}
                onChange={(e) =>
                  setForm({
                    ...form,
                    twitterCard: e.target.value as Page["twitterCard"],
                  })
                }
              >
                <option value="summary_large_image">Immagine grande</option>
                <option value="summary">Riepilogo compatto</option>
              </select>
            </label>
          </>
        )}
        {tab === "advanced" && (
          <>
            <label>
              URL canonical
              <input
                type="url"
                value={form.canonicalUrl || ""}
                onChange={(e) =>
                  setForm({ ...form, canonicalUrl: e.target.value })
                }
                placeholder="Generato automaticamente se vuoto"
              />
            </label>
            <label>
              Dati strutturati JSON-LD
              <textarea
                className="code-field"
                value={structuredData}
                onChange={(e) => setStructuredData(e.target.value)}
                spellCheck={false}
              />
              <small>
                Fino a 5 markup. Il contenuto deve essere una lista JSON.
              </small>
            </label>
            {form.isHome && (
              <label>
                Verifica Google Search Console
                <input
                  value={googleVerification}
                  onChange={(event) =>
                    setGoogleVerification(event.target.value)
                  }
                  placeholder="Token o meta tag fornito da Google"
                />
                <small>
                  Puoi incollare il token oppure l’intero meta tag. Sarà
                  inserito nella homepage alla prossima pubblicazione.
                </small>
              </label>
            )}
            <div className="advanced-note">
              <strong>Automazioni incluse alla pubblicazione</strong>
              <p>
                Canonical automatico, Open Graph, sitemap.xml, robots.txt e
                metadati essenziali vengono generati dal sistema.
              </p>
            </div>
          </>
        )}
        <div className="drawer-actions">
          <button type="button" className="secondary" onClick={close}>
            Annulla
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "Salvataggio…" : "Salva SEO"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SiteWorkspace({ user }: { user: User }) {
  const { siteId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [site, setSite] = useState<Site | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [editing, setEditing] = useState<Page | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [section, setSection] = useState<WorkspaceSection>(() =>
    sectionFromSearch(location.search),
  );
  function openSection(next: WorkspaceSection) {
    if (next === "bookings") setBookingInitialDate("");
    setSection(next);
    const url = next === "home" ? `/sites/${siteId}` : `/sites/${siteId}?section=${next}`;
    if (`${location.pathname}${location.search}` !== url) navigate(url, { replace: true });
  }
  const [members, setMembers] = useState<Member[]>([]);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [activationLink, setActivationLink] = useState("");
  const activationDialogRef = useAccessibleDialog<HTMLDivElement>(
    Boolean(activationLink),
    () => setActivationLink(""),
  );
  const [siteSaving, setSiteSaving] = useState(false);
  const [bookingInitialDate, setBookingInitialDate] = useState("");
  useEffect(() => {
    Promise.all([
      api<{ site: Site }>(`/api/sites/${siteId}`),
      api<{ pages: Page[] }>(`/api/sites/${siteId}/pages`),
    ])
      .then(([s, p]) => {
        setSite(s.site);
        setPages(p.pages);
      })
      .catch((e) => setError(e.message));
  }, [siteId]);
  useEffect(() => {
    if (section === "settings" && user.role === "admin")
      api<{ members: Member[] }>(`/api/sites/${siteId}/members`)
        .then((result) => setMembers(result.members))
        .catch((cause) => setError(cause.message));
  }, [section, siteId, user.role]);
  useEffect(() => {
    if (!["connected"].includes(new URLSearchParams(location.search).get("google") || '') && !["connected"].includes(new URLSearchParams(location.search).get("instagram") || ''))
      return;
    navigate(`/sites/${siteId}`, { replace: true });
  }, [location.search, navigate, siteId]);
  async function createPage(event: FormEvent) {
    event.preventDefault();
    try {
      const r = await api<{ page: Page }>(
        `/api/sites/${siteId}/pages`,
        payload("POST", {
          title: newTitle,
          slug: newTitle
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, ""),
        }),
      );
      setPages((old) => [...old, r.page]);
      setNewTitle("");
      setShowNew(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore");
    }
  }
  async function duplicate(page: Page) {
    const r = await api<{ page: Page }>(
      `/api/pages/${page.id}/duplicate`,
      payload("POST"),
    );
    setPages((old) => [...old, r.page]);
  }
  async function remove(page: Page) {
    if (!confirm(`Eliminare la pagina “${page.title}”?`)) return;
    await api(`/api/pages/${page.id}`, payload("DELETE"));
    setPages((old) => old.filter((item) => item.id !== page.id));
  }
  async function createClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const form = event.currentTarget;
    try {
      const result = await api<{ member: Member; activationUrl: string }>(
        `/api/sites/${siteId}/members`,
        payload("POST", Object.fromEntries(new FormData(form))),
      );
      setMembers((old) => [...old, result.member]);
      setShowMemberForm(false);
      setActivationLink(result.activationUrl);
      form.reset();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Impossibile creare l’accesso",
      );
    }
  }
  async function removeClient(member: Member) {
    if (!confirm(`Rimuovere l’accesso di ${member.name}?`)) return;
    await api(`/api/sites/${siteId}/members/${member.id}`, payload("DELETE"));
    setMembers((old) => old.filter((item) => item.id !== member.id));
  }
  async function generateActivation(member: Member) {
    try {
      const result = await api<{ activationUrl: string }>(
        `/api/sites/${siteId}/members/${member.id}/activation`,
        payload("POST"),
      );
      setActivationLink(result.activationUrl);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Impossibile generare il link",
      );
    }
  }
  async function saveSiteSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSiteSaving(true);
    setError("");
    const values = new FormData(event.currentTarget);
    try {
      const result = await api<{ site: Site }>(
        `/api/sites/${siteId}`,
        payload("PATCH", {
          name: String(values.get("name") || "").trim(),
          ...(admin
            ? {
                domain: String(values.get("domain") || "").trim() || null,
                cloudflareProjectName:
                  String(values.get("cloudflareProjectName") || "").trim() || null,
              }
            : {}),
        }),
      );
      setSite({ ...result.site, editorMode: site?.editorMode, modules: site?.modules });
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Impossibile salvare il sito",
      );
    } finally {
      setSiteSaving(false);
    }
  }
  async function changeSiteStatus(status: "draft" | "archived") {
    if (!site) return;
    const message =
      status === "archived"
        ? `Archiviare «${site.name}»? Sparisce dall'elenco attivo ma non perde nulla: si può riportare in bozza.`
        : `Riportare «${site.name}» in bozza?`;
    if (!window.confirm(message)) return;
    setError("");
    try {
      const result = await api<{ site: Site }>(
        `/api/sites/${siteId}`,
        payload("PATCH", { status }),
      );
      setSite({ ...result.site, editorMode: site.editorMode, modules: site.modules });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Impossibile aggiornare lo stato");
    }
  }
  async function deleteSitePermanently() {
    if (!site) return;
    const typed = window.prompt(
      `Eliminare per sempre «${site.name}» con pagine, contenuti e accessi?\nNon si può annullare. Per confermare scrivi il nome del sito: ${site.name}`,
    );
    if (typed === null) return;
    if (typed.trim() !== site.name) {
      setError("Il nome scritto non coincide: sito non eliminato.");
      return;
    }
    setError("");
    try {
      await api(`/api/sites/${siteId}?permanent=1`, payload("DELETE"));
      navigate("/", { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Impossibile eliminare il sito");
    }
  }
  const seoChecks = pages.flatMap((page) => [
    { page, label: "Titolo SEO", complete: Boolean(page.seoTitle) },
    {
      page,
      label: "Meta description",
      complete: Boolean(page.metaDescription),
    },
    {
      page,
      label: "Condivisione social",
      complete: Boolean(page.ogImageUrl && (page.ogTitle || page.seoTitle)),
    },
    {
      page,
      label: "Dati strutturati",
      complete: page.structuredData.length > 0,
    },
  ]);
  const seoScore = seoChecks.length
    ? Math.round(
        (seoChecks.filter((check) => check.complete).length /
          seoChecks.length) *
          100,
      )
    : 0;
  if (!site && !error)
    return (
      <main className="content">
        <div className="workspace-loading">Caricamento progetto…</div>
      </main>
    );
  const admin = user.role === "admin";
  const canEdit =
    admin || site?.memberRole === "admin" || site?.memberRole === "editor";
  const hasModule = (module: NonNullable<Site["modules"]>[number]) => site?.modules?.includes(module) || false;
  type NavItem = { id: typeof section; label: string; icon: Parameters<typeof Icon>[0]["name"] };
  const navGroups: { label: string; items: NavItem[] }[] = [
    { label: "Panoramica", items: [
      { id: "home", label: "Home", icon: "home" },
      { id: "analytics", label: "Statistiche", icon: "chart" },
    ] },
    { label: "Attività", items: [
      ...(hasModule("short_rental") ? [{ id: "rental", label: "Prenotazioni case", icon: "calendar" } as NavItem] : []),
      ...(hasModule("escape_bookings") ? [{ id: "escape", label: "Prenotazioni ed eventi", icon: "hourglass" } as NavItem]
        : hasModule("restaurant_bookings") ? [{ id: "bookings", label: "Prenotazioni", icon: "calendar" } as NavItem] : []),
      ...(hasModule("restaurant_menu") ? [{ id: "menu", label: "Menu ristorante", icon: "menu" } as NavItem] : []),
      ...(hasModule("customer_crm") ? [{ id: "marketing", label: "Clienti e marketing", icon: "users" } as NavItem] : []),
    ] },
    { label: "Sito", items: [
      { id: "pages", label: "Modifica il sito", icon: "pages" },
      ...(hasModule("social_gallery") ? [{ id: "social", label: "Lavori e Instagram", icon: "image" } as NavItem] : []),
    ] },
    { label: "Crescita", items: canEdit ? [{ id: "seo", label: "SEO e GEO", icon: "search" }] : [] },
    { label: "Amministrazione", items: admin ? [{ id: "settings", label: "Impostazioni", icon: "settings" }] : [] },
  ];
  const currentLabel = navGroups.flatMap((group) => group.items).find((item) => item.id === section)?.label || site?.name || "Progetto";
  return (
    <>
      <header className="workspace-bar">
        <button
          className="back"
          onClick={() => navigate("/")}
          aria-label="Torna ai siti"
        >
          ‹
        </button>
        <div>
          <p className="ws-crumbs"><button onClick={() => navigate("/")}>{admin ? "Siti" : "Il tuo sito"}</button><span>/</span>{site?.name || "Progetto"}</p>
          <h1>{currentLabel}</h1>
        </div>
        <span className={`status ${site?.status}`}>
          {site?.status === "published" ? "Online" : "Bozza"}
        </span>
        <div className="workspace-actions">
          <button
            className="secondary"
            aria-label="Apri l’anteprima del sito"
            disabled={!site?.domain}
            onClick={() =>
              site?.domain &&
              window.open(
                `https://${site.domain}`,
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            <Icon name="globe" />
            Anteprima
          </button>
        </div>
      </header>
      <main className="workspace">
        <aside className="workspace-nav" aria-label="Gestione del sito">
          <div className="ws-site-card">
            <button className="ws-back" onClick={() => navigate("/")}><Icon name="back" />{admin ? "Tutti i siti" : "Il tuo sito"}</button>
            <strong>{site?.name || "Progetto"}</strong>
            <span className={`status ${site?.status}`}>{site?.status === "published" ? "Online" : "Bozza"}</span>
            {site?.domain && <small>{site.domain}</small>}
          </div>
          {navGroups.map((group) => group.items.length > 0 && (
            <div className="ws-nav-group" key={group.label}>
              <p className="ws-nav-label">{group.label}</p>
              {group.items.map((item) => (
                <button key={item.id} className={section === item.id ? "active" : ""} aria-current={section === item.id ? "page" : undefined}
                  onClick={() => openSection(item.id)}>
                  <Icon name={item.icon} />
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </aside>
        <section className="workspace-content">
          {error && <Notice message={error} />}
          {section === "home" && site && (
            <SiteHome site={site} userName={user.name} canEdit={canEdit} onNavigate={(next) => openSection(next)} onOpenBooking={(date) => { setBookingInitialDate(date); openSection("bookings"); }} />
          )}
          {section === "analytics" && site && (
            <AnalyticsPanel site={site} onOpenSeo={() => openSection("seo")} />
          )}
          {section === "pages" && (
            <>
              <div className="workspace-title">
                <div>
                  <p className="eyebrow indigo">Sito</p>
                  <h2>
                    {canEdit ? "Modifica il sito" : "Consulta il sito"}
                  </h2>
                  <p>
                    {canEdit
                      ? "Apri l’editor, fai tutte le modifiche e premi Pubblica quando sono pronte. Prima di quel click il sito online non cambia."
                      : "Apri l’anteprima protetta per consultare il sito senza modificarlo."}
                  </p>
                </div>
                <div className="workspace-title-actions">
                  {hasModule("restaurant_menu") && (
                    <button className="primary" onClick={() => openSection("menu")}>
                      <Icon name="menu" />
                      Modifica il menu
                    </button>
                  )}
                  {admin && site?.editorMode !== "protected" && (
                    <button className="primary" onClick={() => setShowNew(true)}>
                      <Icon name="plus" />
                      Nuova pagina
                    </button>
                  )}
                </div>
              </div>
              {hasModule("restaurant_menu") && (
                <button type="button" className="site-menu-jump" onClick={() => openSection("menu")}>
                  <span>
                    <strong>Menu del locale</strong>
                    <small>Piatti, drink, prezzi e disponibilità. Si aggiorna sul sito senza toccare le pagine.</small>
                  </span>
                  <em>Apri modifica menu</em>
                </button>
              )}
              {admin && site?.editorMode !== "protected" && showNew && (
                <form className="inline-create" onSubmit={createPage}>
                  <input
                    autoFocus
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Nome della nuova pagina"
                    required
                  />
                  <button className="primary">Aggiungi</button>
                  <button type="button" onClick={() => setShowNew(false)}>
                    Annulla
                  </button>
                </form>
              )}
              <div className="page-table">
                <div className="table-head">
                  <span>Pagina</span>
                  <span>Stato</span>
                  <span>Navigazione</span>
                  <span />
                </div>
                {pages.map((page) => (
                  <div className="page-row" key={page.id}>
                    <button
                      className="page-name"
                      onClick={() =>
                        navigate(`/sites/${siteId}/editor/${page.id}`)
                      }
                    >
                      <span>{page.isHome ? "⌂" : "□"}</span>
                      <div>
                        <strong>{page.title}</strong>
                        <small>{page.isHome ? "/" : `/${page.slug}`}</small>
                      </div>
                    </button>
                    <span
                      className={`status ${site?.editorMode === "protected" ? "published" : page.status}`}
                    >
                      {site?.editorMode === "protected"
                        ? "Online"
                        : page.status === "published"
                          ? "Pubblicata"
                          : page.status === "hidden"
                            ? "Nascosta"
                            : "Bozza"}
                    </span>
                    <span className="nav-value">
                      {page.inNavigation ? "Visibile" : "Nascosta"}
                    </span>
                    <div className="row-actions">
                      {hasModule("restaurant_menu") && isMenuPage(page) && (
                        <button
                          className="edit-page"
                          onClick={() => openSection("menu")}
                        >
                          Modifica il menu
                        </button>
                      )}
                      <button
                        className={hasModule("restaurant_menu") && isMenuPage(page) ? "edit-page-secondary" : "edit-page"}
                        onClick={() =>
                          navigate(`/sites/${siteId}/editor/${page.id}`)
                        }
                      >
                        {hasModule("restaurant_menu") && isMenuPage(page)
                          ? "Modifica pagina"
                          : canEdit
                            ? "Modifica"
                            : "Apri"}
                      </button>
                      {admin && site?.editorMode !== "protected" && (
                        <button
                          title="Duplica"
                          aria-label={`Duplica ${page.title}`}
                          onClick={() => duplicate(page)}
                        >
                          <Icon name="duplicate" />
                        </button>
                      )}
                      {admin && site?.editorMode !== "protected" && (
                        <button
                          title="Impostazioni SEO"
                          aria-label={`Impostazioni SEO di ${page.title}`}
                          onClick={() => setEditing(page)}
                        >
                          <Icon name="settings" />
                        </button>
                      )}
                      {admin && site?.editorMode !== "protected" && (
                        <button
                          title="Elimina"
                          aria-label={`Elimina ${page.title}`}
                          disabled={page.isHome}
                          onClick={() => remove(page)}
                        >
                          <Icon name="trash" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {section === "seo" && (
            <>
              <div className="workspace-title">
                <div>
                  <p className="eyebrow indigo">Visibilità organica</p>
                  <h2>SEO e GEO</h2>
                  <p>Controlla indicizzazione, anteprime e dati strutturati.</p>
                </div>
              </div>
              {site && (
                <SeoIntelligencePanel site={site} unlocked />
              )}
              {site && <LiveSeoPanel site={site} admin={admin} />}
              {site?.editorMode !== "protected" && (
                <div className="seo-dashboard">
                  <div className="seo-score">
                    <div
                      style={
                        {
                          "--score": `${seoScore * 3.6}deg`,
                        } as React.CSSProperties
                      }
                    >
                      <strong>{seoScore}%</strong>
                    </div>
                    <section>
                      <p className="eyebrow">Stato configurazione</p>
                      <h3>
                        {seoScore === 100
                          ? "Ottimizzazione completata"
                          : "Completa le impostazioni essenziali"}
                      </h3>
                      <p>
                        {seoChecks.filter((check) => !check.complete).length}{" "}
                        attività rimaste sulle pagine del sito.
                      </p>
                    </section>
                  </div>
                  {admin && <div className="seo-tool-grid">
                    <article>
                      <span>01</span>
                      <h3>Checklist SEO</h3>
                      <p>
                        Verifica pagina per pagina i campi che aiutano motori di
                        ricerca e sistemi IA.
                      </p>
                    </article>
                    <article>
                      <span>02</span>
                      <h3>Redirect URL</h3>
                      <p>
                        La struttura dati supporta redirect 301 e 302 e conserva
                        gli URL precedenti.
                      </p>
                    </article>
                    <article>
                      <span>03</span>
                      <h3>Sitemap e robots</h3>
                      <p>
                        Vengono generati automaticamente e potranno essere
                        personalizzati per sito.
                      </p>
                    </article>
                  </div>}
                  <div className="seo-audit">
                    <div className="seo-audit-head">
                      <h3>Problemi da risolvere</h3>
                      <span>
                        {seoChecks.filter((check) => !check.complete).length}
                      </span>
                    </div>
                    {seoChecks
                      .filter((check) => !check.complete)
                      .map((check) => (
                        <button
                          key={`${check.page.id}-${check.label}`}
                          onClick={() => setEditing(check.page)}
                        >
                          <span>!</span>
                          <div>
                            <strong>{check.label}</strong>
                            <small>{check.page.title}</small>
                          </div>
                          <Icon name="arrow" />
                        </button>
                      ))}
                    {seoChecks.every((check) => check.complete) && (
                      <div className="seo-complete">
                        Tutte le pagine hanno le impostazioni SEO principali.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          {section === "menu" && (
            <RestaurantMenuManager
              siteId={siteId}
              canEdit={canEdit}
              siteBaseUrl={site?.domain ? `https://${site.domain}` : `https://${site?.slug}.pages.dev`}
            />
          )}
          {section === "bookings" && (
            <RestaurantOperations key={`bookings-${bookingInitialDate}`} siteId={siteId} canEdit={canEdit} initialTab="agenda" initialDate={bookingInitialDate || undefined} />
          )}
          {section === "marketing" && (
            <RestaurantOperations key="marketing" siteId={siteId} canEdit={canEdit} initialTab="marketing" />
          )}
          {section === "rental" && site && (
            <RentalOperations key={siteId} siteId={siteId} canEdit={canEdit} />
          )}
          {section === "escape" && site && (
            <EscapeOperations siteId={siteId} siteSlug={site.slug} canEdit={canEdit} />
          )}
          {section === "social" && site && (
            <SocialGalleryManager siteId={siteId} canEdit={canEdit} />
          )}
          {section === "settings" && (
            <>
              {site && <ModulesPanel siteId={siteId} initial={site.modules || []} onSaved={(modules) => setSite({ ...site, modules })} />}
              {site && (
                <form
                  className="member-form site-config-form"
                  onSubmit={saveSiteSettings}
                >
                  <div>
                    <p className="eyebrow indigo">Sito e pubblicazione</p>
                    <h3>Indirizzo del progetto</h3>
                    <p className="muted">
                      Il dominio alimenta anteprima, SEO live e Analytics. Il
                      progetto identifica la pubblicazione Cloudflare Pages.
                    </p>
                  </div>
                  <div className="field-pair">
                    <label>
                      Nome sito
                      <input name="name" defaultValue={site.name} required />
                    </label>
                    <label>
                      Dominio pubblico
                      <input
                        name="domain"
                        defaultValue={site.domain || ""}
                        readOnly={!admin}
                        title={admin ? undefined : "Lo può cambiare solo l'agenzia"}
                        placeholder="esempio.it"
                      />
                    </label>
                  </div>
                  <label>
                    Progetto Cloudflare Pages
                    <input
                      name="cloudflareProjectName"
                      defaultValue={site.cloudflareProjectName || ""}
                      readOnly={!admin}
                      title={admin ? undefined : "Lo può cambiare solo l'agenzia"}
                      placeholder="nome-progetto"
                    />
                  </label>
                  <div className="modal-actions">
                    <button className="primary" disabled={siteSaving}>
                      {siteSaving ? "Salvataggio…" : "Salva configurazione"}
                    </button>
                  </div>
                </form>
              )}
              {site && admin && (
                <div className="member-form site-danger-zone">
                  <div>
                    <p className="eyebrow indigo">Solo amministratore</p>
                    <h3>Archivia o elimina il sito</h3>
                    <p className="muted">
                      {site.status === "published"
                        ? "Il sito è online: per eliminarlo va prima archiviato. L'archiviazione si può annullare."
                        : "Un sito non pubblicato (bozza o archiviato) si può eliminare per sempre, con pagine, contenuti e accessi."}
                    </p>
                  </div>
                  <div className="modal-actions">
                    {site.status !== "archived" && (
                      <button type="button" className="secondary" onClick={() => changeSiteStatus("archived")}>
                        Archivia sito
                      </button>
                    )}
                    {site.status === "archived" && (
                      <button type="button" className="secondary" onClick={() => changeSiteStatus("draft")}>
                        Riporta in bozza
                      </button>
                    )}
                    {site.status !== "published" && (
                      <button type="button" className="danger-button" onClick={deleteSitePermanently}>
                        Elimina definitivamente
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="workspace-title">
                <div>
                  <p className="eyebrow indigo">Configurazione</p>
                  <h2>Accessi cliente</h2>
                  <p>
                    Invita il cliente con un link monouso e assegna i permessi.
                  </p>
                </div>
                <button
                  className="primary"
                  onClick={() => setShowMemberForm(true)}
                >
                  <Icon name="plus" />
                  Nuovo accesso
                </button>
              </div>
              {showMemberForm && (
                <form className="member-form" onSubmit={createClient}>
                  <div className="field-pair">
                    <label>
                      Nome cliente
                      <input name="name" minLength={2} required />
                    </label>
                    <label>
                      Email
                      <input name="email" type="email" required />
                    </label>
                  </div>
                  <label>
                    Permesso
                    <select name="role" defaultValue="editor">
                      <option value="editor">Può modificare</option>
                      <option value="viewer">Sola visualizzazione</option>
                    </select>
                  </label>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => setShowMemberForm(false)}
                    >
                      Annulla
                    </button>
                    <button className="primary">Crea link sicuro</button>
                  </div>
                </form>
              )}
              <div className="member-list">
                <div className="member-head">
                  <span>Utente</span>
                  <span>Permesso</span>
                  <span />
                </div>
                {members.map((member) => (
                  <div className="member-row" key={member.id}>
                    <span className="member-avatar">
                      {member.name.slice(0, 1).toUpperCase()}
                    </span>
                    <div>
                      <strong>{member.name}</strong>
                      <small>{member.email}</small>
                    </div>
                    <span className="member-role">
                      {member.role === "admin"
                        ? "Amministratore"
                        : member.role === "editor"
                          ? "Può modificare"
                          : "Sola lettura"}
                    </span>
                    <div className="member-buttons">
                      <button
                        title="Genera nuovo link sicuro"
                        aria-label={`Genera nuovo link sicuro per ${member.name}`}
                        disabled={member.id === user.id}
                        onClick={() => generateActivation(member)}
                      >
                        <Icon name="globe" />
                      </button>
                      <button
                        title="Rimuovi accesso"
                        aria-label={`Rimuovi accesso di ${member.name}`}
                        disabled={member.id === user.id}
                        onClick={() => removeClient(member)}
                      >
                        <Icon name="trash" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </main>
      {editing && (
        <PageEditor
          page={editing}
          advanced={admin}
          close={() => setEditing(null)}
          saved={(page) => {
            setPages((old) =>
              old.map((item) => (item.id === page.id ? page : item)),
            );
            setEditing(null);
          }}
        />
      )}
      {activationLink && (
        <div className="modal-backdrop">
          <div
            ref={activationDialogRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="activation-dialog-title"
            tabIndex={-1}
          >
            <button
              className="modal-close"
              onClick={() => setActivationLink("")}
              aria-label="Chiudi link di attivazione"
            >
              ×
            </button>
            <p className="eyebrow indigo">Link monouso</p>
            <h2 id="activation-dialog-title">Invito pronto</h2>
            <p className="muted">
              Invialo al cliente con un canale sicuro. Scade tra 24 ore e il
              precedente accesso è stato invalidato.
            </p>
            <label>
              Link di attivazione
              <input
                readOnly
                value={activationLink}
                onFocus={(event) => event.currentTarget.select()}
              />
            </label>
            <div className="modal-actions">
              <button
                className="secondary"
                onClick={() => setActivationLink("")}
              >
                Chiudi
              </button>
              <button
                className="primary"
                onClick={() => navigator.clipboard.writeText(activationLink)}
              >
                Copia link
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function App() {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.allSettled([
      api<{ user: User | null }>("/api/auth/session"),
      api<{ needsSetup: boolean }>("/api/setup/status"),
    ])
      .then(([session, setup]) => {
        if (session.status === "fulfilled") setUser(session.value.user);
        if (setup.status === "fulfilled") setNeedsSetup(setup.value.needsSetup);
        else setNeedsSetup(false);
      })
      .finally(() => setLoading(false));
  }, []);
  async function logout() {
    try {
      await api("/api/auth/logout", payload("POST"));
    } finally {
      setUser(null);
    }
  }
  const view = useMemo(() => {
    if (loading || needsSetup === null)
      return (
        <div className="app-loading">
          <img src={universeMark} alt="Universe" />
          <p>Preparazione workspace</p>
        </div>
      );
    const activationToken = location.pathname.match(
      /^\/activate\/([^/]+)$/,
    )?.[1];
    if (activationToken)
      return <ActivationScreen token={activationToken} onAuth={setUser} />;
    if (!user) {
      if (location.pathname === "/setup" && needsSetup)
        return (
          <AuthScreen
            mode="setup"
            onAuth={(next) => {
              setUser(next);
              setNeedsSetup(false);
            }}
          />
        );
      return <AuthScreen mode="login" onAuth={setUser} />;
    }
    if (/^\/sites\/[^/]+\/editor\/[^/]+$/.test(location.pathname))
      return (
        <Routes>
          <Route
            path="/sites/:siteId/editor/:pageId"
            element={<ClientEditor user={user} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      );
    return (
      <Shell user={user} onLogout={logout}>
        <Routes>
          <Route path="/" element={<Dashboard user={user} />} />
          <Route
            path="/leads"
            element={user.role === "admin" ? <LeadsPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/leads/:leadId"
            element={user.role === "admin" ? <LeadsPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/sites/:siteId"
            element={<SiteWorkspace user={user} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
    );
  }, [loading, location.pathname, needsSetup, user]);
  return view;
}
